import { Card, Box, Typography, Theme } from '@mui/material';
import { useGetIdentity, usePermissions } from 'react-admin';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import PrintIcon from '@mui/icons-material/Print';
import EventIcon from '@mui/icons-material/Event';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Link } from 'react-router-dom';
import { User } from '../types';

const styles = {
  container: {
    padding: { xs: 2, sm: 3, md: 4 },
  },
  welcomeCard: {
    marginBottom: { xs: 2, md: 3 },
    background: (theme: Theme) =>
      theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, ${theme.palette.grey[800]} 0%, ${theme.palette.grey[900]} 100%)`
        : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    borderLeft: (theme: Theme) => `4px solid ${theme.palette.primary.main}`,
    padding: { xs: 2, sm: 3, md: 4 },
  },
  welcomeTitle: {
    color: (theme: Theme) =>
      theme.palette.mode === 'dark'
        ? theme.palette.text.primary
        : theme.palette.primary.contrastText,
    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
    fontWeight: 700,
  },
  quickAccessContainer: {
    marginTop: { xs: 3, md: 4 },
  },
  sectionTitle: {
    marginBottom: { xs: 2, md: 3 },
    color: 'text.primary',
    fontWeight: 600,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, 1fr)',
      md: 'repeat(3, 1fr)',
    },
    gap: { xs: 2, md: 3 },
    alignItems: 'stretch',
    maxWidth: '720px',
    margin: '0 auto',
  },
  actionCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.5,
    padding: 2,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    bgcolor: 'background.paper',
    aspectRatio: '1',
    minHeight: 0,
    maxWidth: '200px',
    maxHeight: '200px',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: 6,
      bgcolor: 'action.hover',
    },
  },
  icon: {
    fontSize: { xs: 56, md: 64 },
    color: 'primary.main',
  },
  cardTitle: {
    fontWeight: 700,
    color: 'text.primary',
    fontSize: { xs: '1rem', md: '1.1rem' },
    textAlign: 'center',
  },
  cardDescription: {
    fontSize: '0.75rem',
    color: 'text.secondary',
    textAlign: 'center',
    marginTop: 0.5,
  },
};

const Dashboard = () => {
  const { data: identity } = useGetIdentity();
  const { permissions } = usePermissions();
  const user = identity as User | undefined;

  return (
    <Box sx={styles.container}>
      {/* Welcome Section */}
      <Card sx={styles.welcomeCard}>
        <Typography component="h1" sx={styles.welcomeTitle}>
          Welcome, {user?.username || 'User'}!
        </Typography>
      </Card>

      {/* Quick Access Panels */}
      <Box sx={styles.quickAccessContainer}>
        <Typography variant="h5" component="h2" sx={styles.sectionTitle}>
          Quick Access
        </Typography>
        <Box sx={styles.cardsGrid}>
          {permissions === 'Admin' && (
            <>
              <Card component={Link} to="/groups" sx={styles.actionCard}>
                <GroupsIcon sx={styles.icon} />
                <Box>
                  <Typography variant="h6" sx={styles.cardTitle}>
                    Groups
                  </Typography>
                  <Typography sx={styles.cardDescription}>Manage groups</Typography>
                </Box>
              </Card>

              <Card component={Link} to="/users" sx={styles.actionCard}>
                <PeopleIcon sx={styles.icon} />
                <Box>
                  <Typography variant="h6" sx={styles.cardTitle}>
                    Users
                  </Typography>
                  <Typography sx={styles.cardDescription}>Manage users</Typography>
                </Box>
              </Card>
            </>
          )}

          <Card component={Link} to="/machines" sx={styles.actionCard}>
            <PrecisionManufacturingIcon sx={styles.icon} />
            <Box>
              <Typography variant="h6" sx={styles.cardTitle}>
                Machines
              </Typography>
              <Typography sx={styles.cardDescription}>View machines</Typography>
            </Box>
          </Card>

          <Card component={Link} to="/printings" sx={styles.actionCard}>
            <PrintIcon sx={styles.icon} />
            <Box>
              <Typography variant="h6" sx={styles.cardTitle}>
                Printings
              </Typography>
              <Typography sx={styles.cardDescription}>View printings</Typography>
            </Box>
          </Card>

          <Card component={Link} to="/events" sx={styles.actionCard}>
            <EventIcon sx={styles.icon} />
            <Box>
              <Typography variant="h6" sx={styles.cardTitle}>
                Events
              </Typography>
              <Typography sx={styles.cardDescription}>View events</Typography>
            </Box>
          </Card>

          <Card component={Link} to="/tags" sx={styles.actionCard}>
            <LocalOfferIcon sx={styles.icon} />
            <Box>
              <Typography variant="h6" sx={styles.cardTitle}>
                Tags
              </Typography>
              <Typography sx={styles.cardDescription}>View tags</Typography>
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
